import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  submissions: {}, // Grouped by quiz ID: { quizId: [submission1, submission2, ...] }
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalSubmissions: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  },
  availableTags: [],
  flaggedQuestions: {}, // Map of submission_question keys to flag data
  lastFetched: null, // Timestamp of last fetch
  isInitialized: false // Flag to track if data has been fetched at least once
};

const submissionsSlice = createSlice({
  name: 'submissions',
  initialState,
  reducers: {
    setSubmissions: (state, action) => {
      const { submissions, pagination, availableTags } = action.payload;
      
      // Group submissions by quiz
      const grouped = {};
      submissions.forEach(sub => {
        const quizId = sub.quiz._id;
        if (!grouped[quizId]) {
          grouped[quizId] = [];
        }
        grouped[quizId].push(sub);
      });
      
      // Sort each group by attempt number
      Object.keys(grouped).forEach(quizId => {
        grouped[quizId].sort((a, b) => a.attemptNumber - b.attemptNumber);
      });
      
      state.submissions = grouped;
      state.pagination = pagination;
      state.availableTags = availableTags;
      state.lastFetched = Date.now();
      state.isInitialized = true;
    },
    
    addSubmission: (state, action) => {
      const newSubmission = action.payload;
      const quizId = newSubmission.quiz._id;
      
      // Add or update the submission in the grouped structure
      if (!state.submissions[quizId]) {
        state.submissions[quizId] = [];
      }
      
      // Check if this submission already exists (by attemptNumber)
      const existingIndex = state.submissions[quizId].findIndex(
        sub => sub.attemptNumber === newSubmission.attemptNumber
      );
      
      if (existingIndex >= 0) {
        // Update existing submission
        state.submissions[quizId][existingIndex] = newSubmission;
      } else {
        // Add new submission
        state.submissions[quizId].push(newSubmission);
        // Sort by attempt number
        state.submissions[quizId].sort((a, b) => a.attemptNumber - b.attemptNumber);
      }
      
      // Update total submissions count
      state.pagination.totalSubmissions += 1;
      
      // Update available tags if new tags are present
      if (newSubmission.quiz && newSubmission.quiz.tags) {
        const existingTagValues = state.availableTags.map(tag => tag.value);
        newSubmission.quiz.tags.forEach(tag => {
          if (tag && tag.name && !existingTagValues.includes(tag.name)) {
            state.availableTags.push({ value: tag.name, label: tag.name });
          }
        });
        // Sort tags
        state.availableTags.sort((a, b) => a.value.localeCompare(b.value));
      }
    },
    
    setFlaggedQuestions: (state, action) => {
      state.flaggedQuestions = action.payload;
    },
    
    addFlag: (state, action) => {
      const { submissionId, questionId, flag } = action.payload;
      const key = `${submissionId}_${questionId}`;
      state.flaggedQuestions[key] = flag;
    },
    
    updatePagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    clearSubmissions: (state) => {
      return initialState;
    }
  }
});

export const {
  setSubmissions,
  addSubmission,
  setFlaggedQuestions,
  addFlag,
  updatePagination,
  clearSubmissions
} = submissionsSlice.actions;

export default submissionsSlice.reducer;
